import { useMemo } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { AGENT_MODELS_QUERY_KEY } from "src/agents/queries/management";
import { credentialsApi } from "src/api/endpoints";
import { useCurrentWorkspace } from "src/api/queries";
import type {
  CredentialItem,
  CredentialListResponse,
  CredentialTestResult,
  CredentialUpdateRequest,
  ModelProvider,
} from "src/clients/agent-management";

const PREFIX_CACHE_KEY = "agent-credentials";

const credentialKeys = {
  all: [PREFIX_CACHE_KEY] as const,
  list: () => [...credentialKeys.all, "list"] as const,
};

/**
 * Returns a CredentialsApi client for the current workspace.
 */
const useCredentialsClient = () => {
  const { data: workspace } = useCurrentWorkspace();

  return useMemo(
    () =>
      workspace?.base_url ? credentialsApi(workspace.base_url) : undefined,
    [workspace?.base_url],
  );
};

/**
 * Fetches all credentials (metadata only, no secrets).
 */
export const useCredentials = () => {
  const client = useCredentialsClient();

  return useQuery({
    queryKey: credentialKeys.list(),
    queryFn: async (): Promise<CredentialListResponse> => {
      if (!client) {
        throw new Error("Credentials client not available");
      }
      const response =
        await client.listCredentialsAgentsManagementApiV0SettingsCredentialsGet();
      return response.data;
    },
    enabled: !!client,
  });
};

type CreateCredentialParams = {
  provider: ModelProvider;
  data: { [key: string]: unknown };
};

/**
 * Creates a new credential using the UUID-based endpoint.
 */
export const useCreateCredential = () => {
  const client = useCredentialsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      provider,
      data,
    }: CreateCredentialParams): Promise<CredentialItem> => {
      if (!client) {
        throw new Error("Credentials client not available");
      }
      const response =
        await client.createCredentialAgentsManagementApiV0SettingsCredentialsPost(
          { provider, requestBody: data },
        );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: credentialKeys.all });
      queryClient.invalidateQueries({ queryKey: AGENT_MODELS_QUERY_KEY });
    },
  });
};

type UpdateCredentialByIdParams = {
  credentialId: string;
  data: CredentialUpdateRequest;
};

/**
 * Updates a credential by ID (name, models, enabled, and optionally secrets).
 */
export const useUpdateCredentialById = () => {
  const client = useCredentialsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      credentialId,
      data,
    }: UpdateCredentialByIdParams): Promise<CredentialItem> => {
      if (!client) {
        throw new Error("Credentials client not available");
      }
      const response =
        await client.updateCredentialByIdAgentsManagementApiV0SettingsCredentialsCredentialIdPut(
          { credentialId, credentialUpdateRequest: data },
        );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: credentialKeys.all });
      queryClient.invalidateQueries({ queryKey: AGENT_MODELS_QUERY_KEY });
    },
  });
};

type DeleteCredentialByIdParams = {
  credentialId: string;
};

export const useDeleteCredentialById = () => {
  const client = useCredentialsClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      credentialId,
    }: DeleteCredentialByIdParams): Promise<void> => {
      if (!client) {
        throw new Error("Credentials client not available");
      }
      await client.deleteCredentialByIdAgentsManagementApiV0SettingsCredentialsCredentialIdDelete(
        { credentialId },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: credentialKeys.all });
      queryClient.invalidateQueries({ queryKey: AGENT_MODELS_QUERY_KEY });
    },
  });
};

type TestCredentialByIdParams = {
  credentialId: string;
};

export const useTestCredentialById = () => {
  const client = useCredentialsClient();

  return useMutation({
    mutationFn: async ({
      credentialId,
    }: TestCredentialByIdParams): Promise<CredentialTestResult> => {
      if (!client) {
        throw new Error("Credentials client not available");
      }
      const response =
        await client.runConnectivityTestAgentsManagementApiV0SettingsCredentialsCredentialIdTestPost(
          { credentialId },
        );
      return response.data;
    },
  });
};
